import { useMemo } from 'react';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { FormatContent } from 'src/contexts/ContentContext';
import { MultiSelectScope } from 'src/contexts/MultiSelectionContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { Stack } from 'src/core/ui';
import { FormatOption } from 'src/modals/EditTableColumns';
import { ListingTableFlattened } from 'src/tables/ListingTable';
import {
  ListingTableColumnId,
  ListingTableFlattenedColumnId,
} from 'src/utils/columns/inventory/inventoryColumnUtils.types';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import { BulkEditPreviewWithDetails } from 'src/WebApiController';

export const BulkEditPreviewListingDetails = ({
  preview,
  listingIds,
  compact,
}: {
  preview?: BulkEditPreviewWithDetails | null;
  /**
   * POS-7295: restrict the table to these listing ids. Used to show which listings a merge group covers.
   */
  listingIds?: number[];
  /**
   * POS-7295: compact inline rendering — no fixed height, no "max items" warning, minimal columns.
   */
  compact?: boolean;
}) => {
  const { data: catalog } = useCatalogDataContext();

  const listings = useMemo(() => {
    return preview?.listings
      ?.filter((l) => listingIds == null || listingIds.includes(l.id))
      .map((l) => {
        const { event, performer, venue } = getEventPerformerVenue(
          l?.viagVirtId,
          catalog
        );

        if (event) {
          return { event: event.event, listing: l, performer, venue };
        }
        return null;
      })
      .filter((l) => l != null)
      .map((l) => l!);
  }, [catalog, listingIds, preview?.listings]);

  if (!listings) {
    return null;
  }

  return (
    <Stack
      direction="column"
      style={compact ? undefined : { height: '50vh' }}
      gap="l"
      alignItems="center"
    >
      {!compact &&
        listings.length <
          (preview?.preview?.bulkEditCountPreview?.Listing?.totalCount ??
            0) && (
          <WarningMessage
            message={
              <FormatContent
                id={FormatContentId.BulkEditMaxItemPreviewDisplayMessage}
                params={[
                  `${listings.length}`,
                  `${preview?.preview?.bulkEditCountPreview?.Listing?.totalCount}`,
                ]}
              />
            }
          />
        )}
      <ListingTableFlattened
        listings={listings}
        useVirtuoso={!compact}
        disablePagination
        disableSelection
        listingCount={listings.length}
        ungroupedListingCount={listings.length}
        listingGroupCount={0}
        failedToRetrieveData={false}
        columnVisibilityOverride={
          compact
            ? [ListingTableColumnId.ListingId, ListingTableColumnId.Seating]
            : [
                ListingTableColumnId.ListingId,
                ListingTableFlattenedColumnId.Event,
                ListingTableFlattenedColumnId.EventDate,
                ListingTableColumnId.Seating,
                ListingTableColumnId.Actions,
              ]
        }
        formatOptionOverrides={{
          [ListingTableColumnId.Seating]: FormatOption.Seating_FullSeating,
          [ListingTableFlattenedColumnId.Event]: FormatOption.Event_EventName,
          [ListingTableFlattenedColumnId.EventDate]:
            FormatOption.DateTime_Month_DD_YY_Time,
          [ListingTableColumnId.Actions]: FormatOption.Actions_LinkOnly,
        }}
        selectionMode={{
          mode: MultiSelectScope.AllGroups,
        }}
        withOuterPadding={false}
      />
    </Stack>
  );
};
